import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface FilterOption<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
}

/**
 * A compact filter dropdown (Jira-style): a labelled trigger showing the current
 * selection, and a popover of options with a check on the active one. Highlights
 * when a non-default value is selected. Closes on outside-click / Escape.
 */
export function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
  defaultValue,
}: {
  label: string;
  value: T;
  options: FilterOption<T>[];
  onChange: (v: T) => void;
  /** The "no filter" value — when `value` differs, the trigger reads as active. */
  defaultValue?: T;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = options.find((o) => o.value === value) ?? options[0];
  const isFiltered = defaultValue != null && value !== defaultValue;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors',
          isFiltered
            ? 'border-iris/50 bg-iris/10 text-bone'
            : 'border-hairline text-haze hover:border-border-strong hover:text-bone',
        )}
      >
        <span className="text-muted">{label}:</span>
        <span className="font-semibold">{active?.label}</span>
        <span className="text-[0.55rem] text-muted">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 z-20 mt-1 min-w-[190px] overflow-hidden rounded-md border border-hairline bg-surface py-1 shadow-pop"
        >
          {options.map((o) => (
            <button
              key={o.value}
              role="menuitemradio"
              aria-checked={o.value === value}
              onClick={() => {
                setOpen(false);
                onChange(o.value);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-surface-2',
                o.value === value ? 'text-bone' : 'text-haze',
              )}
            >
              <span className="w-3 shrink-0 text-iris">{o.value === value ? '✓' : ''}</span>
              <span className="flex-1 truncate">{o.label}</span>
              {o.count != null && <span className="text-[0.65rem] text-muted">{o.count}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
